const apiKey = (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined)?.trim() || '';

export const GOOGLE_MAPS_MAP_ID =
  (import.meta.env.VITE_GOOGLE_MAPS_MAP_ID as string | undefined)?.trim() || 'DEMO_MAP_ID';
export const GOOGLE_MAPS_AUTH_FAILURE_EVENT = 'bentonit:google-maps-auth-failure';
export const isGoogleMapsConfigured = Boolean(apiKey);

export interface GoogleLatLngBounds {
  extend(position: { lat: number; lng: number }): void;
}

export interface GoogleMapInstance {
  fitBounds(bounds: GoogleLatLngBounds, padding?: number): void;
  setMapTypeId(mapTypeId: string): void;
}

export interface GoogleAdvancedMarker {
  map: GoogleMapInstance | null;
  content?: HTMLElement;
  title?: string;
  addEventListener(event: string, listener: () => void): void;
}

export interface GoogleMarkerLibrary {
  AdvancedMarkerElement: new (options: Record<string, unknown>) => GoogleAdvancedMarker;
}

export interface GoogleMapsNamespace {
  maps: {
    Map: new (element: HTMLElement, options: Record<string, unknown>) => GoogleMapInstance;
    LatLngBounds: new () => GoogleLatLngBounds;
    importLibrary(name: 'marker'): Promise<GoogleMarkerLibrary>;
  };
}

type MapsWindow = Window & {
  google?: GoogleMapsNamespace;
  gm_authFailure?: () => void;
  __bentonitGoogleMapsReady?: () => void;
  __bentonitGoogleMapsAuthInstalled?: boolean;
};

let mapsPromise: Promise<GoogleMapsNamespace> | null = null;

function installAuthFailureBridge(): void {
  const target = window as MapsWindow;
  if (target.__bentonitGoogleMapsAuthInstalled) return;
  target.__bentonitGoogleMapsAuthInstalled = true;
  const previous = target.gm_authFailure;
  target.gm_authFailure = () => {
    previous?.();
    window.dispatchEvent(new Event(GOOGLE_MAPS_AUTH_FAILURE_EVENT));
  };
}

export function loadGoogleMaps(): Promise<GoogleMapsNamespace> {
  if (!isGoogleMapsConfigured) return Promise.reject(new Error('Google Maps API key is not configured'));
  if (typeof window === 'undefined') return Promise.reject(new Error('Google Maps requires a browser'));

  const target = window as MapsWindow;
  installAuthFailureBridge();
  if (target.google?.maps) return Promise.resolve(target.google);
  if (mapsPromise) return mapsPromise;

  mapsPromise = new Promise<GoogleMapsNamespace>((resolve, reject) => {
    const callback = '__bentonitGoogleMapsReady';
    const rejectLoad = () => {
      mapsPromise = null;
      reject(new Error('Google Maps failed to load'));
    };
    target[callback] = () => {
      delete target[callback];
      if (target.google?.maps) resolve(target.google);
      else rejectLoad();
    };

    const params = new URLSearchParams({
      key: apiKey,
      loading: 'async',
      callback,
      v: 'weekly',
      libraries: 'marker',
      region: 'AM',
      auth_referrer_policy: 'origin',
    });
    if (GOOGLE_MAPS_MAP_ID !== 'DEMO_MAP_ID') params.set('map_ids', GOOGLE_MAPS_MAP_ID);

    const script = document.createElement('script');
    script.id = 'bentonit-google-maps';
    script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
    script.async = true;
    script.onerror = rejectLoad;
    document.head.appendChild(script);
  });

  return mapsPromise;
}

export function makeMapMarker(
  label: string,
  tone: 'context' | 'verified' | 'provisional' = 'context',
  compact = false,
): HTMLElement {
  const marker = document.createElement('div');
  marker.className = [
    'geo-map-marker',
    `geo-map-marker--${tone}`,
    compact ? 'geo-map-marker--compact' : '',
  ].filter(Boolean).join(' ');
  marker.setAttribute('aria-label', label);
  const dot = document.createElement('span');
  const text = document.createElement('strong');
  text.textContent = label;
  marker.append(dot, text);
  return marker;
}
